import { FC, useState } from 'react';
import { Card, Row, Col, InputNumber, Button, Space, Typography } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';
import { FXRates, Currency } from '../EU6Calculator.types';

const { Text } = Typography;

const StyledCard = styled(Card)`
  height: 100%;
  border-radius: 8px;
  background: ${APP_SHELL_THEME.panelBg};
  border-color: ${APP_SHELL_THEME.panelBorder};

  .ant-card-head {
    background: ${APP_SHELL_THEME.filterInputBg};
    border-bottom: 1px solid ${APP_SHELL_THEME.panelBorder};
    border-radius: 8px 8px 0 0;
    padding: 16px 24px;
  }

  .ant-card-head-title {
    color: rgba(255, 255, 255, 0.92);
    font-weight: 600;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const RateRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${APP_SHELL_THEME.filterInputBg};
  border-radius: 6px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const RateLabel = styled(Text)`
  font-weight: 600;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  min-width: 140px;
`;

const RateValue = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BaseText = styled(Text)`
  font-size: 14px;
  color: ${APP_SHELL_THEME.textMuted};
`;

const EqualsText = styled(Text)`
  font-size: 14px;
  color: ${APP_SHELL_THEME.textMuted};
  margin: 0 8px;
`;

interface FXRatesCardProps {
  fxRates: FXRates;
  onUpdate: (rates: Partial<FXRates>) => void;
}

export const FXRatesCard: FC<FXRatesCardProps> = ({ fxRates, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedRates, setEditedRates] = useState<FXRates>(fxRates);

  const handleEdit = () => {
    setEditedRates(fxRates);
    setIsEditing(true);
  };

  const handleSave = () => {
    onUpdate(editedRates);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedRates(fxRates);
    setIsEditing(false);
  };

  const handleRateChange = (currency: Currency, value: number | null) => {
    if (value !== null && value > 0) {
      setEditedRates((prev) => ({ ...prev, [currency]: value }));
    }
  };

  const displayRates = isEditing ? editedRates : fxRates;

  return (
    <StyledCard
      title={<>📊 FX Rates (Base: GBP)</>}
      extra={
        isEditing ? (
          <Space>
            <Button type="primary" size="small" icon={<SaveOutlined />} onClick={handleSave}>
              Save
            </Button>
            <Button size="small" icon={<CloseOutlined />} onClick={handleCancel}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={handleEdit}
            style={{ color: 'white' }}
          >
            Edit Rates
          </Button>
        )
      }
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <RateRow>
            <RateLabel>1 GBP</RateLabel>
            <RateValue>
              <EqualsText>=</EqualsText>
              {isEditing ? (
                <InputNumber
                  style={{ width: '100%' }}
                  value={displayRates[Currency.GBP]}
                  disabled
                  min={0}
                  step={0.01}
                  precision={4}
                />
              ) : (
                <BaseText strong>{displayRates[Currency.GBP].toFixed(4)} GBP (Base)</BaseText>
              )}
            </RateValue>
          </RateRow>
        </Col>

        <Col span={24}>
          <RateRow>
            <RateLabel>1 EUR</RateLabel>
            <RateValue>
              <EqualsText>=</EqualsText>
              {isEditing ? (
                <InputNumber
                  style={{ width: '100%' }}
                  value={displayRates[Currency.EUR]}
                  onChange={(value) => handleRateChange(Currency.EUR, value)}
                  min={0}
                  step={0.01}
                  precision={4}
                  placeholder="Enter EUR rate"
                />
              ) : (
                <Text strong>{displayRates[Currency.EUR].toFixed(4)} GBP</Text>
              )}
            </RateValue>
          </RateRow>
        </Col>

        <Col span={24}>
          <RateRow>
            <RateLabel>1 USD</RateLabel>
            <RateValue>
              <EqualsText>=</EqualsText>
              {isEditing ? (
                <InputNumber
                  style={{ width: '100%' }}
                  value={displayRates[Currency.USD]}
                  onChange={(value) => handleRateChange(Currency.USD, value)}
                  min={0}
                  step={0.01}
                  precision={4}
                  placeholder="Enter USD rate"
                />
              ) : (
                <Text strong>{displayRates[Currency.USD].toFixed(4)} GBP</Text>
              )}
            </RateValue>
          </RateRow>
        </Col>

        <Col span={24}>
          <RateRow>
            <RateLabel>1 CAD</RateLabel>
            <RateValue>
              <EqualsText>=</EqualsText>
              {isEditing ? (
                <InputNumber
                  style={{ width: '100%' }}
                  value={displayRates[Currency.CAD]}
                  onChange={(value) => handleRateChange(Currency.CAD, value)}
                  min={0}
                  step={0.01}
                  precision={4}
                  placeholder="Enter CAD rate"
                />
              ) : (
                <Text strong>{displayRates[Currency.CAD].toFixed(4)} GBP</Text>
              )}
            </RateValue>
          </RateRow>
        </Col>
      </Row>

      {/* Cross rates display */}
      <Row gutter={[16, 16]} style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <Col span={24}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              1 EUR = {(displayRates[Currency.EUR] / displayRates[Currency.USD]).toFixed(4)} USD
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              1 GBP = {(displayRates[Currency.GBP] / displayRates[Currency.USD]).toFixed(4)} USD
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              1 GBP = {(displayRates[Currency.GBP] / displayRates[Currency.EUR]).toFixed(4)} EUR
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              1 GBP = {(displayRates[Currency.GBP] / displayRates[Currency.CAD]).toFixed(4)} CAD
            </Text>
          </Space>
        </Col>
      </Row>
    </StyledCard>
  );
};
